// Browser DOM snapshot (docs/04, best-effort PWA path). The current <img>/<video>
// on the visible layer is drawn to an offscreen canvas at its natural size, the
// active corner logo is composited on top, and the result is exported as JPEG.
// Media come from same-origin blob: URLs so the canvas is never tainted.
//
// This is the only browser-only, non-unit-tested piece of the snapshot feature
// (canvas + real decoded pixels don't exist under jsdom/node); the DECISION of
// whether a frame is capturable, and the native/debounce logic, live in
// snapshot.ts and are fully tested there.

import type { DomCapture } from './snapshot.ts';
import type { Manifest } from './types.ts';

const MAX_WIDTH = 1920;
const MAX_BYTES = 2 * 1024 * 1024;

/** Decode a base64 jpeg (native path) into a Blob without a data: round-trip. */
export function base64ToJpegBlob(base64: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: 'image/jpeg' });
}

/** The <img>/<video> on the currently visible (opacity 1) layer, or null. */
function visibleMedia(stage: HTMLElement): HTMLImageElement | HTMLVideoElement | null {
  const layers = Array.from(stage.querySelectorAll<HTMLElement>('.layer'));
  const front = layers.find((l) => l.style.opacity === '1') ?? layers[0];
  const el = front?.querySelector<HTMLElement>('.media');
  if (el instanceof HTMLImageElement || el instanceof HTMLVideoElement) return el;
  return null;
}

function naturalSize(el: HTMLImageElement | HTMLVideoElement): { w: number; h: number } {
  if (el instanceof HTMLVideoElement) return { w: el.videoWidth, h: el.videoHeight };
  return { w: el.naturalWidth, h: el.naturalHeight };
}

/** Draw the active corner logo over the frame (best-effort; skipped on any error). */
function drawOverlayLogo(ctx: CanvasRenderingContext2D, stage: HTMLElement, manifest: Manifest | null): void {
  const logo = manifest?.overlay?.logo;
  if (!logo) return;
  const img = stage.querySelector<HTMLImageElement>('.overlay-logo img, img.overlay-logo');
  if (!(img instanceof HTMLImageElement) || !img.complete || img.naturalWidth === 0) return;
  try {
    const cw = ctx.canvas.width;
    const ch = ctx.canvas.height;
    const w = (logo.size_pct / 100) * cw;
    const h = w * (img.naturalHeight / img.naturalWidth);
    const margin = 0.02 * cw;
    const x = logo.position === 'tl' || logo.position === 'bl' ? margin : cw - w - margin;
    const y = logo.position === 'tl' || logo.position === 'tr' ? margin : ch - h - margin;
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, logo.opacity / 100));
    ctx.drawImage(img, x, y, w, h);
    ctx.restore();
  } catch {
    /* tainted/undecoded logo — leave the frame without the watermark */
  }
}

function toJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', quality));
}

/**
 * Rasterize the current frame to a JPEG (≤1920 wide, ≤2 MB). Returns null when
 * nothing capturable is on screen or the export fails.
 */
export async function captureCurrentFrame(
  stage: HTMLElement,
  manifest: Manifest | null,
): Promise<DomCapture | null> {
  const el = visibleMedia(stage);
  if (!el) return null;
  const { w: nw, h: nh } = naturalSize(el);
  if (!nw || !nh) return null;

  const scale = Math.min(1, MAX_WIDTH / nw);
  const width = Math.round(nw * scale);
  const height = Math.round(nh * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  try {
    ctx.drawImage(el, 0, 0, width, height);
  } catch {
    return null; // tainted (should not happen with same-origin blobs) or not ready
  }
  drawOverlayLogo(ctx, stage, manifest);

  let blob = await toJpegBlob(canvas, 0.8);
  if (blob && blob.size > MAX_BYTES) blob = await toJpegBlob(canvas, 0.6);
  if (!blob) return null;
  return { blob, width, height };
}
